<template>
  <div class="sf-root">
    <!-- ══ Screen: the shareable landing page ═══════════════════════════ -->
    <div v-show="!sheetOnly" class="sf-screen">
      <!-- Rep bar. Only when the link carried a code — an unattributed visitor gets
           the EzFix'd line in the footer instead, not an empty bar. -->
      <div v-if="rep" class="sf-repbar">
        <span class="sf-repbar-label">Your EzFix'd contact</span>
        <b>{{ rep.name }}</b>
        <a v-if="rep.phone" :href="'tel:' + telHref(rep.phone)">{{ rep.phone }}</a>
        <a v-if="rep.email" :href="'mailto:' + rep.email">{{ rep.email }}</a>
      </div>

      <div class="sf-wrap">
        <header class="sf-mast">
          <span class="sf-logo">Ez<b>Fix'd</b></span>
          <span class="sf-badge">For repair shops · {{ REGION }}</span>
        </header>

        <section class="sf-hero">
          <div class="sf-hero-copy">
            <h1 class="sf-h1">Turn “maybe later” into more repair work.</h1>
            <p class="sf-kicker">EzFix'd payment solutions<br />for Canadian repair shops</p>
            <p class="sf-lede">{{ LEDE }}</p>

            <div class="sf-cta">
              <a class="sf-btn sf-btn-primary" href="#talk">Sign up now</a>
              <a class="sf-btn sf-btn-link" href="#how">Learn more</a>
            </div>

            <ul class="sf-ticks">
              <li v-for="t in TICKS" :key="t">
                <svg class="sf-ico sf-ico-tick" viewBox="0 0 24 24" aria-hidden="true">
                  <path v-for="(d, k) in ICONS.check" :key="k" :d="d" />
                </svg>
                <span>{{ t }}</span>
              </li>
            </ul>
          </div>

          <!-- Stands in for the product shot the printed piece cannot carry: drawn
               rather than photographed so it prints at any size and ships no asset. -->
          <aside class="sf-visual" aria-hidden="true">
            <div class="sf-device">
              <div class="sf-device-bar">
                <i /><i /><i />
                <span class="sf-device-name">Ez<b>Fix'd</b></span>
              </div>
              <div class="sf-device-body">
                <div class="sf-device-row">
                  <span class="sf-pill">Approved</span>
                  <span class="sf-device-time">~{{ DECISION_MINUTES }} min</span>
                </div>
                <div class="sf-device-lines">
                  <i style="width: 86%" /><i style="width: 64%" /><i style="width: 72%" />
                </div>
                <div class="sf-device-foot">
                  <i style="width: 40%" /><span class="sf-device-cta" />
                </div>
              </div>
            </div>
          </aside>
        </section>

        <section class="sf-cards">
          <div v-for="t in TILES" :key="t.title" class="sf-card">
            <span class="sf-cardico">
              <svg class="sf-ico" viewBox="0 0 24 24" aria-hidden="true">
                <path v-for="(d, k) in ICONS[t.icon]" :key="k" :d="d" />
              </svg>
            </span>
            <h3>{{ t.title }}</h3>
            <p>{{ t.body }}</p>
          </div>

          <div class="sf-card sf-card-stat">
            <div class="sf-stat-label">Trusted approvals</div>
            <div class="sf-gaugewrap">
              <svg class="sf-gauge" viewBox="0 0 120 68" aria-hidden="true">
                <path class="sf-gauge-track" d="M10 60a50 50 0 0 1 100 0" />
                <path
                  class="sf-gauge-fill"
                  d="M10 60a50 50 0 0 1 100 0"
                  :stroke-dasharray="gaugeDash"
                />
              </svg>
              <div class="sf-gauge-val">{{ APPROVAL_RATE }}</div>
            </div>
            <div class="sf-stat-sub">Approval rate</div>
            <p class="sf-stat-cap">{{ STAT_CAPTION }}</p>
          </div>
        </section>
      </div>

      <section id="how" class="sf-band">
        <div class="sf-wrap">
          <h2 class="sf-h2 sf-h2-center">How it works</h2>
          <div class="sf-steps">
            <div v-for="(s, i) in STEPS" :key="s.title" class="sf-step">
              <div class="sf-step-title">{{ s.title }}</div>
              <div class="sf-step-art">
                <svg class="sf-ico sf-ico-lg" viewBox="0 0 24 24" aria-hidden="true">
                  <path v-for="(d, k) in ICONS[s.icon]" :key="k" :d="d" />
                </svg>
                <span class="sf-step-n">{{ i + 1 }}</span>
              </div>
              <p>{{ s.body }}</p>
              <span v-if="i < STEPS.length - 1" class="sf-step-arrow" aria-hidden="true">
                <svg class="sf-ico" viewBox="0 0 24 24">
                  <path v-for="(d, k) in ICONS.arrow" :key="k" :d="d" />
                </svg>
              </span>
            </div>
          </div>
          <p class="sf-howfoot"><b>That's it.</b> {{ HOW_FOOT }}</p>
        </div>
      </section>

      <!-- ── Win more work / talk to a rep ────────────────────────────── -->
      <section id="talk" class="sf-wrap sf-talk">
        <div class="sf-talk-grid">
          <div>
            <h2 class="sf-h2">Win more work, boost your margin</h2>
            <p class="sf-talk-lede">
              Tell us where to reach you and
              {{ rep ? rep.firstName + ' will' : "an EzFix'd rep will" }}
              call to set your shop up. Setting up takes one conversation.
            </p>

            <div v-if="rep" class="sf-repcard">
              <div class="sf-repcard-name">
                {{ rep.name }}
              </div>
              <div class="sf-repcard-role">EzFix'd business development</div>
              <div v-if="rep.phone" class="sf-repcard-line">
                <a :href="'tel:' + telHref(rep.phone)">{{ rep.phone }}</a>
              </div>
              <div v-if="rep.email" class="sf-repcard-line">
                <a :href="'mailto:' + rep.email">{{ rep.email }}</a>
              </div>
            </div>
            <div v-else class="sf-repcard">
              <div class="sf-repcard-name">EzFix'd</div>
              <div class="sf-repcard-line">
                <a :href="'tel:' + telHref(PHONE)">{{ PHONE }}</a>
              </div>
              <div class="sf-repcard-line">
                <a :href="'mailto:' + EMAIL">{{ EMAIL }}</a>
              </div>
            </div>
          </div>

          <form class="sf-form" @submit.prevent="submit">
            <div v-if="sent" class="sf-sent">
              <b>Thank you — we have your details.</b>
              <span>{{ sentBy }} will be in touch shortly.</span>
            </div>

            <template v-else>
              <label>
                Shop name *
                <input v-model.trim="form.shop_name" type="text" required />
              </label>
              <label>
                Your name *
                <input v-model.trim="form.contact_name" type="text" required />
              </label>
              <div class="sf-form-row">
                <label>
                  Phone *
                  <input v-model.trim="form.phone" type="tel" required />
                </label>
                <label>
                  City
                  <input v-model.trim="form.city" type="text" />
                </label>
              </div>
              <label>
                Email
                <input v-model.trim="form.email" type="email" />
              </label>
              <label>
                Anything we should know?
                <textarea v-model.trim="form.message" rows="3" />
              </label>

              <!-- Honeypot. Hidden from people and from screen readers, plausible to a
                   bot, and the server discards anything that arrives with it filled. -->
              <label class="sf-hp" aria-hidden="true">
                Website
                <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
              </label>

              <button
                class="sf-btn sf-btn-primary sf-btn-block"
                type="submit"
                :disabled="sending || !formValid"
              >
                {{ sending ? 'Sending…' : 'Request a callback' }}
              </button>
              <div v-if="error" class="sf-formerr">
                {{ error }}
              </div>
              <p class="sf-formnote">
                For repair shops and dealerships. This is not a credit application — your customers
                apply themselves, from their own phone.
              </p>
            </template>
          </form>
        </div>
      </section>

      <footer class="sf-foot">
        <div class="sf-wrap">
          <div class="sf-foot-top">
            <span class="sf-logo sf-logo-sm">Ez<b>Fix'd</b></span>
            <span>{{ SITE }}</span>
            <span>{{ PHONE }}</span>
            <span>{{ EMAIL }}</span>
          </div>
          <p class="sf-legal">
            {{ LEGAL }}
          </p>
        </div>
      </footer>
    </div>

    <!-- The dashboard preview and PDF use this same handout. Keep its claims shared
         with the landing page and its dimensions within both A4 and Letter. -->
    <div class="sf-sheet" :class="{ 'sf-sheet-onscreen': sheetOnly }">
      <div class="sf-sheet-inner">
        <div class="sf-sheet-head">
          <div>
            <span class="sf-logo">Ez<b>Fix'd</b></span>
            <span class="sf-sheet-tagline">Repair now. Pay over time.</span>
          </div>
          <div class="sf-sheet-edition">
            Built for repair shops<span>{{ REGION }}</span>
          </div>
        </div>

        <div class="sf-sheet-hero">
          <div class="sf-sheet-hero-copy">
            <p class="sf-sheet-eyebrow">Give your customers a way to say yes</p>
            <h1 class="sf-sheet-h1">
              Turn “maybe later”<br />into <em>more<br />repair work.</em>
            </h1>
            <p class="sf-sheet-lede">{{ LEDE }}</p>
          </div>
          <div class="sf-sheet-photo">
            <img :src="shopPhoto" alt="A service advisor reviewing repairs beside a car" />
            <div class="sf-sheet-photo-caption">
              <svg class="sf-ico" viewBox="0 0 24 24" aria-hidden="true">
                <path v-for="(d, k) in ICONS.check" :key="k" :d="d" />
              </svg>
              <span>You focus on the repair.<br /><b>We handle the payments.</b></span>
            </div>
          </div>
        </div>

        <div class="sf-sheet-proof">
          <div><strong>$0</strong><span>Cost to your shop</span></div>
          <div><strong>In full</strong><span>Your shop gets paid</span></div>
          <div>
            <strong>~{{ DECISION_MINUTES }} min</strong><span>For most application decisions</span>
          </div>
        </div>

        <div class="sf-sheet-benefits">
          <h2 class="sf-sheet-h2">Keep the job. Keep your margin.</h2>
          <div class="sf-sheet-cards">
            <div v-for="t in TILES" :key="'pc-' + t.title" class="sf-sheet-card">
              <svg class="sf-ico" viewBox="0 0 24 24" aria-hidden="true">
                <path v-for="(d, k) in ICONS[t.icon]" :key="k" :d="d" />
              </svg>
              <h3>{{ t.title }}</h3>
              <p>{{ t.body }}</p>
            </div>
          </div>
        </div>

        <div class="sf-sheet-band">
          <div class="sf-sheet-sectionhead">
            <h2 class="sf-sheet-h2">Easy at the service counter.</h2>
            <span>Three simple steps</span>
          </div>
          <div class="sf-sheet-steps">
            <div v-for="(s, i) in STEPS" :key="'ps-' + s.title" class="sf-sheet-step">
              <div class="sf-sheet-stephead">
                <span class="sf-sheet-stepnum">0{{ i + 1 }}</span>
                <h3>{{ s.title }}</h3>
              </div>
              <p>{{ s.body }}</p>
            </div>
          </div>
          <p class="sf-sheet-howfoot">No terminal. No subscription. No monthly minimum.</p>
        </div>

        <div class="sf-sheet-close">
          <div class="sf-sheet-close-copy">
            <p class="sf-sheet-eyebrow">Let's get your shop set up</p>
            <h2 class="sf-sheet-closeh">Your next repair.<br />A new way to say yes.</h2>
            <p class="sf-sheet-closesub">It starts with one conversation.</p>
            <a class="sf-sheet-apply" :href="'https://' + printUrl + '#talk'">
              <span>Explore EzFix'd for your shop</span>
              <svg class="sf-ico" viewBox="0 0 24 24" aria-hidden="true">
                <path v-for="(d, k) in ICONS.arrow" :key="k" :d="d" />
              </svg>
            </a>
          </div>
          <div class="sf-sheet-repblock">
            <span class="sf-sheet-contactlabel">Your EzFix'd contact</span>
            <b class="sf-sheet-repname">{{ rep ? rep.name : "EzFix'd team" }}</b>
            <a class="sf-sheet-repphone" :href="'tel:' + telHref((rep && rep.phone) || PHONE)">
              {{ (rep && rep.phone) || PHONE }}
            </a>
            <a class="sf-sheet-repline" :href="'mailto:' + ((rep && rep.email) || EMAIL)">
              {{ (rep && rep.email) || EMAIL }}
            </a>
            <a class="sf-sheet-applyurl" :href="'https://' + printUrl">{{ printUrl }}</a>
          </div>
        </div>

        <div class="sf-sheet-footline">
          <span>Ez<b>Fix'd</b> · Payment solutions for Canadian repair shops</span>
          <span>Financing subject to approval.</span>
        </div>
        <p class="sf-sheet-legal">
          {{ LEGAL }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import shopPhoto from '../assets/images/automotive-service-advisor.png'

/**
 * The shop-facing one-pager a BDM shares — /shops, or /shops/<code> when it carries a
 * rep's attribution code.
 *
 * Audience is a BUSINESS: a repair shop deciding whether to offer EzFix'd to its
 * customers. It is deliberately NOT a consumer page. There is no rate table, no
 * calculator, no application — a borrower's credit conversation happens inside the
 * customer flow, never on a page a partner's staff hands out (§2.2 of the referral
 * agreement). The form below collects a shop, and the only thing it does is email the
 * rep. The device drawn in the hero carries no figures for the same reason.
 *
 * Three ways in, all the same component:
 *   /shops/<code>            what a BDM posts and emails
 *   /shops/<code>?view=print the one-page handout, on screen (the dashboard preview)
 *   /shops/<code>?print=1    the same, and opens the print dialog
 */

// ── Claims. One block, because these are the lines that get challenged and they must
// be changed in one place — the page and the printed handout read from the same
// constants so a flyer in a glovebox can never disagree with the live page.
const APPROVAL_RATE = '84%'
const DECISION_MINUTES = 5
const REGION = 'British Columbia'
const PHONE = '604-245-6680'
const EMAIL = 'info@ezfixd.com'
const SITE = 'ezfixd.com'
const LEDE =
  "Help customers move ahead with the repairs they need. With EzFix'd, they pay " +
  'over time. Your shop gets paid in full.'
const HOW_FOOT =
  'We handle the rest. Approval is instant for your customer, and you are paid directly.'
const STAT_CAPTION =
  'Most customer applications are answered instantly, based on average application data.'
const LEGAL =
  "EzFix'd is operated by Mechanic Click Services Inc. Financing is subject to " +
  "approval. Rates, terms and eligibility are set by EzFix'd and are disclosed to " +
  'the customer in their own agreement. Approval figures are based on average ' +
  'application data and are not a guarantee of approval for any individual customer.'

// ── Line art. Paths on a 24×24 grid, stroked in currentColor, so the same icon is the
// same shape at 20px on screen and at 5mm on paper. Deliberately not a sprite and not
// a webfont: <use> and icon fonts both have print paths where they silently render
// nothing, and this sheet is only ever judged as a PDF.
const ICONS = {
  growth: ['M4 20h16', 'M4 20V4', 'M7 15l3.5-3.5 3 3L19 7', 'M15 7h4v4'],
  paid: [
    'M3 7h18a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1z',
    'M12 9.5a2.5 2.5 0 1 0 0 5a2.5 2.5 0 1 0 0-5',
    'M5.5 11v2',
    'M18.5 11v2',
  ],
  shield: ['M12 3l7 3v5c0 4.2-2.9 7.6-7 8.9C7.9 18.6 5 15.2 5 11V6z', 'M9 12l2 2 4-4'],
  check: ['M12 2.5a9.5 9.5 0 1 0 0 19a9.5 9.5 0 1 0 0-19', 'M8 12.3l2.6 2.6L16 9.5'],
  phone: [
    'M7.5 2.5h9a1.5 1.5 0 0 1 1.5 1.5v16a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V4a1.5 1.5 0 0 1 1.5-1.5z',
    'M9 8h6',
    'M9 11.5h6',
    'M9 15h3.5',
    'M10.5 18.5h3',
  ],
  clock: ['M12 21a8 8 0 1 0 0-16a8 8 0 1 0 0 16', 'M12 9v4l2.5 1.8', 'M9.8 2.5h4.4', 'M12 2.5V5'],
  invoice: ['M6 2.5h8l4 4v15H6z', 'M14 2.5v4h4', 'M8.5 11h7', 'M8.5 14.5h7', 'M8.5 18h4'],
  arrow: ['M4 12h14', 'M13 7l5 5-5 5'],
}

const TILES = [
  {
    icon: 'growth',
    title: 'Close more work',
    body:
      'Give customers a way to move ahead with the full repair, even when the ' +
      'upfront cost is holding them back.',
  },
  {
    icon: 'paid',
    title: 'Get paid directly',
    body:
      "EzFix'd funds the invoice and collects the payments. You get paid in full " +
      'without chasing customer payments.',
  },
  {
    icon: 'shield',
    title: 'Protect your pricing',
    body:
      'Offer payment flexibility without discounting your parts or labour. ' +
      'Keep the value in the work you do.',
  },
]

// The fourth claim from the old tile row. It reads better as a promise next to the
// call to action than as a card competing with the three above.
const TICKS = [
  'No cost to the shop — no terminal, no subscription, no monthly minimum',
  'Your team stays focused on repairs, not on financing paperwork',
]

const STEPS = [
  {
    icon: 'phone',
    title: 'The customer applies',
    body: 'A short application on their own phone. Nothing for your advisor to fill in.',
  },
  {
    icon: 'clock',
    title: 'A decision in minutes',
    body:
      `Most applications are answered in about ${DECISION_MINUTES} minutes, while ` +
      'the customer is still at the counter.',
  },
  {
    icon: 'invoice',
    title: 'You invoice and send',
    body:
      'Upload the invoice and send the contract in one click. Once it is signed, ' +
      "EzFix'd pays the shop.",
  },
]

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap'

export default {
  name: 'ForShops',

  data() {
    return {
      APPROVAL_RATE,
      DECISION_MINUTES,
      REGION,
      PHONE,
      EMAIL,
      SITE,
      LEDE,
      HOW_FOOT,
      STAT_CAPTION,
      LEGAL,
      ICONS,
      TILES,
      TICKS,
      STEPS,
      shopPhoto,

      rep: null,
      form: {
        shop_name: '',
        contact_name: '',
        phone: '',
        email: '',
        city: '',
        message: '',
        // Honeypot — always empty for a person. See the hidden field in the form.
        website: '',
      },
      sending: false,
      sent: false,
      sentBy: '',
      error: '',
    }
  },

  computed: {
    /** Path segment first, ?rep= second — an emailed link sometimes loses the path. */
    code() {
      return String(this.$route.params.code || this.$route.query.rep || '')
        .toLowerCase()
        .trim()
    },

    sheetOnly() {
      return this.$route.query.view === 'print' || this.$route.query.print === '1'
    },

    printUrl() {
      return this.code ? `${SITE}/shops/${this.code}` : `${SITE}/shops`
    },

    /** Dash pattern that fills the gauge arc to the claim, so the two cannot drift. */
    gaugeDash() {
      const arc = Math.PI * 50
      const pct = Math.max(0, Math.min(100, parseFloat(APPROVAL_RATE) || 0)) / 100
      return `${(arc * pct).toFixed(1)} ${arc.toFixed(1)}`
    },

    formValid() {
      return !!(this.form.shop_name && this.form.contact_name && this.form.phone)
    },
  },

  created() {
    this.fontsReady = this.loadFonts()
    // Kept as a promise, not awaited: the page renders on the fallback contact straight
    // away, and only ?print=1 has any reason to wait for the lookup to land.
    this.repLoaded = this.loadRep()
    /* App.vue renders the site footer on every route, so without this the page ends in
     * two footers — and the printed handout runs onto a second page carrying the
     * consumer site's links. Reversed on destroy so the rest of the site is untouched
     * if a visitor navigates on. */
    document.body.classList.add('sf-standalone')
  },

  destroyed() {
    document.body.classList.remove('sf-standalone')
  },

  mounted() {
    // The dashboard's Print button links here with ?print=1. The dialog freezes whatever
    // is on the page at that instant, so wait for the contact, font, photo and a painted
    // frame. A flat timeout can print a partially loaded sheet on a slower connection.
    if (this.$route.query.print === '1') {
      this.whenPrintable().then(() => window.print())
    }
  },

  methods: {
    /**
     * Inter, on demand and for this page only — the rest of the site is set in Poppins
     * and should not pay for a second family. Resolves once the face is actually usable,
     * which is what the print path waits on.
     */
    loadFonts() {
      if (typeof document === 'undefined') return Promise.resolve()
      const settled = () => (document.fonts ? document.fonts.ready : Promise.resolve())
      if (document.getElementById('sf-fonts')) return settled()

      return new Promise((resolve) => {
        const l = document.createElement('link')
        l.id = 'sf-fonts'
        l.rel = 'stylesheet'
        l.href = FONT_HREF
        // Either way: a missing display font is a cosmetic problem, a print button that
        // never opens a dialog is not.
        l.onload = resolve
        l.onerror = resolve
        document.head.appendChild(l)
      }).then(settled)
    },

    /**
     * Resolves once the sheet is worth freezing. Capped, because a print button that
     * never opens a dialog is a worse failure than one that opens over the fallback font.
     */
    whenPrintable() {
      const photosReady = Promise.all(
        Array.from(this.$el.querySelectorAll('.sf-sheet img')).map((img) => {
          if (img.complete) return Promise.resolve()
          return new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true })
            img.addEventListener('error', resolve, { once: true })
          })
        })
      )
      const ready = Promise.all([this.fontsReady, this.repLoaded, photosReady]).catch(() => {})
      const cap = new Promise((resolve) => setTimeout(resolve, 3000))
      return Promise.race([ready, cap]).then(
        () => new Promise((resolve) => this.$nextTick(() => requestAnimationFrame(resolve)))
      )
    },

    /**
     * A missing or stale code is not an error state. The page still sells; it just
     * falls back to the EzFix'd switchboard, so an old printout keeps working after a
     * rep changes territory.
     */
    async loadRep() {
      if (!this.code) return
      try {
        const { data } = await this.axios.get(
          `${process.env.ROOT_API}/marketing/rep/${encodeURIComponent(this.code)}`
        )
        if (data && data.rep) {
          this.rep = {
            ...data.rep,
            firstName: String(data.rep.name || '').split(' ')[0],
          }
        }
      } catch (err) {
        this.rep = null
      }
    },

    telHref(v) {
      return String(v || '').replace(/[^\d+]/g, '')
    },

    async submit() {
      if (!this.formValid || this.sending) return
      this.sending = true
      this.error = ''
      try {
        const { data } = await this.axios.post(`${process.env.ROOT_API}/marketing/shop-lead`, {
          ...this.form,
          rep: this.code || undefined,
        })
        this.sentBy = (data && data.contactName) || "An EzFix'd rep"
        this.sent = true
      } catch (err) {
        const serverMsg = err.response && err.response.data && err.response.data.error
        // The server's own wording on a 429 already names a number to call; anything
        // else gets the generic line, which must never leave the shop with no next step.
        this.error =
          (err.response && err.response.status === 429 && serverMsg) ||
          'We could not send that. Please call ' +
            ((this.rep && this.rep.phone) || PHONE) +
            ' and we will get you set up.'
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
.sf-root {
  --bg: #eceef2;
  --surface: #ffffff;
  --ink: #101827;
  --muted: #5c6675;
  --faint: #8a93a2;
  --line: #dfe4ec;
  --band: #f2f5fa;
  --accent: #1e50c8;
  --accent-soft: #e7effe;
  --ezf: #0e9c6c;
  --sans: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  --disp: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;

  font-family: var(--sans);
  color: var(--ink);
  background: var(--bg);
  min-height: 100vh;
  -webkit-font-smoothing: antialiased;
}

/* A local normalize, because the site's theme sheets style bare elements and this page
   shares none of their design. color-3.css paints every `h1` #009579; the template adds
   `h2,h3{color:#444}`, `h1,h2{text-transform:uppercase;font-weight:300}`,
   `p{color:#777;letter-spacing:.03em;text-transform:capitalize}` and — the expensive one
   — `section{padding:100px 0}`. A rule on the element beats a value inherited from an
   ancestor, so nothing here can be left to the cascade: that is what turned the handout's
   headline theme-green, Title Cased every line of body copy, and opened 100px of dead
   space above and below the card row.

   Both halves are :where(), so the whole selector weighs one class: it out-ranks a bare
   `h1` and every rule below out-ranks it. Split in two because `b` wants its colour taken
   in hand but not its weight. */
:where(.sf-root)
  :where(h1, h2, h3, h4, h5, h6, p, ul, ol, li, section, header, footer, div, span, b) {
  color: inherit;
  letter-spacing: inherit;
  text-transform: none;
}
:where(.sf-root) :where(h1, h2, h3, h4, h5, h6, p, ul, ol, li, section, header, footer) {
  margin: 0;
  padding: 0;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  list-style: none;
}

.sf-wrap {
  max-width: 1060px;
  margin: 0 auto;
  padding: 0 22px;
}

/* ── Marks shared by the page and the sheet ──────────────── */
.sf-logo {
  font-family: var(--disp);
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  color: var(--ink);
}
.sf-logo b {
  color: var(--ezf);
  font-weight: 800;
}
.sf-logo-sm {
  font-size: 17px;
}

.sf-badge {
  font-size: 12px;
  font-weight: 500;
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: 999px;
  padding: 6px 14px;
  white-space: nowrap;
}

.sf-ico {
  width: 20px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.sf-ico-lg {
  width: 34px;
  height: 34px;
  stroke-width: 1.5;
}
.sf-ico-tick {
  width: 17px;
  height: 17px;
  color: var(--ezf);
  flex: none;
}

/* ── Rep bar ─────────────────────────────────────────────── */
.sf-repbar {
  background: var(--ink);
  color: #fff;
  font-size: 13.5px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  align-items: center;
  justify-content: center;
  padding: 9px 22px;
}
.sf-repbar-label {
  color: #9aa5b5;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 11px;
}
.sf-repbar a {
  color: #fff;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);
}

/* ── Masthead ────────────────────────────────────────────── */
.sf-mast {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 0 16px;
  border-bottom: 1px solid var(--line);
}

/* ── Hero ────────────────────────────────────────────────── */
.sf-hero {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 40px;
  align-items: center;
  padding: 34px 0 6px;
}
.sf-h1 {
  font-family: var(--disp);
  font-size: clamp(34px, 5.2vw, 52px);
  line-height: 1.02;
  letter-spacing: -0.025em;
  font-weight: 800;
  text-transform: uppercase;
  margin: 0;
}
.sf-kicker {
  font-family: var(--disp);
  font-size: clamp(17px, 2.2vw, 23px);
  line-height: 1.18;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: -0.005em;
  margin: 14px 0 0;
}
.sf-lede {
  font-size: 16px;
  line-height: 1.5;
  color: var(--muted);
  max-width: 40ch;
  margin: 14px 0 0;
}

.sf-cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 22px;
  margin-top: 22px;
}
.sf-btn {
  display: inline-block;
  border: 1.5px solid transparent;
  border-radius: 8px;
  padding: 13px 30px;
  font-size: 15.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  font-family: var(--sans);
  transition: 0.14s;
}
.sf-btn-primary {
  background: var(--accent);
  color: #fff;
}
.sf-btn-primary:hover {
  background: #16409f;
  color: #fff;
}
.sf-btn-primary:disabled {
  background: #9db6ee;
  cursor: not-allowed;
}
.sf-btn-link {
  padding: 13px 0;
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 3px;
}
.sf-btn-link:hover {
  color: #16409f;
}
.sf-btn-block {
  width: 100%;
  margin-top: 4px;
  padding: 13px 22px;
}

.sf-ticks {
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
}
.sf-ticks li {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  font-size: 14.5px;
  line-height: 1.4;
  margin-top: 9px;
}

/* ── The drawn device ────────────────────────────────────── */
.sf-visual {
  background: var(--band);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 26px;
}
.sf-device {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
}
.sf-device-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 11px;
  background: #f7f9fc;
  border-bottom: 1px solid var(--line);
}
.sf-device-bar i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #cdd5e0;
}
.sf-device-name {
  margin-left: auto;
  font-family: var(--disp);
  font-weight: 800;
  font-size: 11px;
  color: var(--ink);
}
.sf-device-name b {
  color: var(--ezf);
  font-weight: 800;
}
.sf-device-body {
  padding: 14px;
}
.sf-device-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.sf-pill {
  background: #e4f6ee;
  color: #0a7a54;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.sf-device-time {
  font-size: 11.5px;
  color: var(--faint);
}
.sf-device-lines {
  margin-top: 14px;
}
.sf-device-lines i {
  display: block;
  height: 7px;
  border-radius: 4px;
  background: #eef1f6;
  margin-bottom: 8px;
}
.sf-device-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--line);
  padding-top: 12px;
  margin-top: 4px;
}
.sf-device-foot i {
  display: block;
  height: 7px;
  border-radius: 4px;
  background: #eef1f6;
}
.sf-device-cta {
  width: 62px;
  height: 20px;
  border-radius: 5px;
  background: var(--accent);
}

/* ── Cards ───────────────────────────────────────────────── */
.sf-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: 34px;
}
.sf-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 18px;
}
.sf-cardico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 9px;
  background: var(--accent-soft);
  color: var(--accent);
  margin-bottom: 13px;
}
.sf-card h3 {
  font-family: var(--disp);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.015em;
  margin: 0 0 7px;
}
.sf-card p {
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted);
  margin: 0;
}

.sf-card-stat {
  text-align: center;
  background: var(--band);
}
.sf-stat-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--ink);
}
.sf-gaugewrap {
  position: relative;
  margin: 10px auto 0;
  max-width: 150px;
}
.sf-gauge {
  display: block;
  width: 100%;
  height: auto;
}
.sf-gauge-track {
  fill: none;
  stroke: #d7dfeb;
  stroke-width: 11;
  stroke-linecap: round;
}
.sf-gauge-fill {
  fill: none;
  stroke: var(--accent);
  stroke-width: 11;
  stroke-linecap: round;
}
.sf-gauge-val {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2%;
  font-family: var(--disp);
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
}
.sf-stat-sub {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--muted);
  margin-top: 5px;
}
.sf-stat-cap {
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--muted);
  margin: 9px 0 0;
}

/* ── How it works ────────────────────────────────────────── */
.sf-band {
  margin-top: 40px;
  padding: 38px 0 42px;
  background: var(--band);
  border-top: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
}
.sf-h2 {
  font-family: var(--disp);
  font-size: clamp(24px, 3.4vw, 34px);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  line-height: 1.08;
  margin: 0 0 8px;
}
.sf-h2-center {
  text-align: center;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.14em;
  margin-bottom: 26px;
}

.sf-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}
.sf-step {
  position: relative;
  text-align: center;
  padding: 0 6px;
}
.sf-step-title {
  font-family: var(--disp);
  font-size: 13.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  line-height: 1.25;
  min-height: 2.5em;
}
.sf-step-art {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--accent);
  margin: 10px 0 12px;
}
.sf-step-n {
  font-family: var(--disp);
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  color: var(--accent);
  letter-spacing: -0.04em;
}
.sf-step p {
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted);
  margin: 0;
}
.sf-step-arrow {
  position: absolute;
  top: 44px;
  right: -24px;
  color: var(--accent);
}
.sf-step-arrow .sf-ico {
  width: 26px;
  height: 26px;
  stroke-width: 2;
}
.sf-howfoot {
  margin: 26px 0 0;
  text-align: center;
  font-size: 14.5px;
  color: var(--muted);
}
.sf-howfoot b {
  color: var(--ink);
}

/* ── Win more work / talk to a rep ───────────────────────── */
.sf-talk {
  padding: 44px 22px 48px;
}
.sf-talk-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 40px;
  align-items: start;
}
.sf-talk-lede {
  color: var(--muted);
  font-size: 15.5px;
  line-height: 1.55;
  max-width: 44ch;
  margin: 10px 0 22px;
}
.sf-repcard {
  background: var(--surface);
  border: 1px solid var(--line);
  border-left: 3px solid var(--ezf);
  border-radius: 10px;
  padding: 18px 20px;
  display: inline-block;
  min-width: 260px;
}
.sf-repcard-name {
  font-family: var(--disp);
  font-size: 17px;
  font-weight: 700;
}
.sf-repcard-role {
  font-size: 12px;
  color: var(--faint);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-top: 2px;
}
.sf-repcard-line {
  margin-top: 8px;
  font-size: 14.5px;
}
.sf-repcard-line a {
  color: var(--accent);
  text-decoration: none;
}

.sf-form {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 22px;
}
.sf-form label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 12px;
}
.sf-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.sf-form input,
.sf-form textarea {
  display: block;
  width: 100%;
  margin-top: 5px;
  padding: 10px 12px;
  border: 1.5px solid var(--line);
  border-radius: 8px;
  font-size: 14.5px;
  font-weight: 400;
  color: var(--ink);
  font-family: var(--sans);
  outline: none;
  background: var(--surface);
}
.sf-form input:focus,
.sf-form textarea:focus {
  border-color: var(--accent);
}
/* Honeypot. Moved off-canvas rather than display:none — some bots skip fields that are
   not rendered, and this one is meant to be found and filled. */
.sf-hp {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}
.sf-formnote {
  font-size: 11.5px;
  color: var(--faint);
  line-height: 1.45;
  margin: 12px 0 0;
}
.sf-formerr {
  margin-top: 10px;
  font-size: 13px;
  color: #c0392b;
}
.sf-sent {
  text-align: center;
  padding: 28px 8px;
}
.sf-sent b {
  display: block;
  font-family: var(--disp);
  font-size: 17px;
  margin-bottom: 6px;
}
.sf-sent span {
  color: var(--muted);
  font-size: 14.5px;
}

/* ── Footer ──────────────────────────────────────────────── */
.sf-foot {
  background: var(--ink);
  color: #b8c1cf;
  padding: 26px 0 30px;
}
.sf-foot .sf-logo {
  color: #fff;
}
.sf-foot-top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  font-size: 13.5px;
  margin-bottom: 12px;
}
.sf-legal {
  font-size: 11.5px;
  line-height: 1.5;
  color: #7d8798;
  margin: 0;
  max-width: 92ch;
}

/* ── Responsive ──────────────────────────────────────────── */
@media (max-width: 940px) {
  .sf-hero,
  .sf-talk-grid {
    grid-template-columns: 1fr;
    gap: 26px;
  }
  .sf-cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .sf-steps {
    grid-template-columns: 1fr;
    gap: 26px;
  }
  /* .sf-screen, not the bare class: the sheet shares this element, and the print box is
     about 700px wide, so an unscoped rule here silently drops the arrows from the PDF. */
  .sf-screen .sf-step-arrow {
    display: none;
  }
}
@media (max-width: 540px) {
  .sf-cards,
  .sf-form-row {
    grid-template-columns: 1fr;
  }
  .sf-btn-primary {
    width: 100%;
    text-align: center;
  }
}

/* The handout uses a 186mm content area, leaving room for printer margins on
   both A4 and Letter. Preview and print share the same type sizes and layout. */
.sf-sheet {
  display: none;
}
.sf-sheet-onscreen {
  display: block;
  overflow-x: auto;
  background: #e1e6ed;
  padding: 24px 16px;
  min-height: 100vh;
}
.sf-sheet-inner {
  box-sizing: border-box;
  color: var(--ink);
  font-family: var(--sans);
}
.sf-sheet-onscreen .sf-sheet-inner {
  background: #fff;
  width: 210mm;
  min-height: 279.4mm;
  padding: 12mm;
  margin: 0 auto;
  box-shadow: 0 8px 36px rgba(16, 24, 39, 0.15);
}
.sf-sheet-inner *,
.sf-sheet-inner *::before,
.sf-sheet-inner *::after {
  box-sizing: border-box;
}
.sf-sheet-inner .sf-ico {
  width: 5.5mm;
  height: 5.5mm;
  flex-shrink: 0;
}
.sf-sheet-inner a {
  text-decoration: none;
}
.sf-sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 4mm;
}
.sf-sheet-inner .sf-logo {
  font-size: 23pt;
  line-height: 1;
  letter-spacing: -0.06em;
}
.sf-sheet-tagline {
  display: block;
  margin-top: 1.3mm;
  color: var(--muted);
  font-size: 7.5pt;
}
.sf-sheet-edition {
  color: var(--ink);
  font-size: 8pt;
  font-weight: 700;
  text-align: right;
}
.sf-sheet-edition span {
  display: block;
  color: var(--muted);
  font-size: 7.5pt;
  font-weight: 400;
  margin-top: 1mm;
}
.sf-sheet-hero {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  min-height: 62mm;
  overflow: hidden;
  border-radius: 3mm 3mm 0 0;
  background: #102b49;
  color: #fff;
}
.sf-sheet-hero-copy {
  padding: 5mm;
  position: relative;
  z-index: 1;
}
.sf-sheet-eyebrow {
  font-size: 7pt;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.sf-sheet-hero .sf-sheet-eyebrow {
  color: #a9d9f8;
  margin-bottom: 3mm;
}
.sf-sheet-h1 {
  color: #fff;
  font-family: var(--disp);
  font-size: 27pt;
  font-weight: 800;
  letter-spacing: -0.045em;
  line-height: 1.04;
  margin: 0;
  text-transform: none;
}
.sf-sheet-h1 em {
  color: #73e2bc;
  font-style: normal;
}
.sf-sheet-lede {
  font-size: 9pt;
  line-height: 1.5;
  color: #e2ebf5;
  margin-top: 3mm;
  max-width: 90mm;
}
.sf-sheet-photo {
  position: relative;
  min-width: 0;
  background: #173953;
}
.sf-sheet-photo > img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 63% center;
}
.sf-sheet-photo-caption {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  align-items: center;
  gap: 2mm;
  padding: 4mm;
  background: #163f56;
  color: #fff;
  font-size: 7.5pt;
  line-height: 1.5;
}
.sf-sheet-photo-caption .sf-ico {
  color: #73e2bc;
}
.sf-sheet-photo-caption b {
  color: #fff;
  font-weight: 700;
}
.sf-sheet-proof {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #eaf3fa;
  border: 1px solid #dae7f0;
  border-top: 0;
  border-radius: 0 0 3mm 3mm;
  padding: 3mm 0;
}
.sf-sheet-proof > div {
  padding: 0 4mm;
  text-align: center;
}
.sf-sheet-proof > div + div {
  border-left: 1px solid #c9dbe9;
}
.sf-sheet-proof strong {
  display: block;
  color: #16436b;
  font-size: 22pt;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.1;
}
.sf-sheet-proof span {
  display: block;
  margin-top: 1.3mm;
  color: #3d586f;
  font-size: 7.5pt;
  line-height: 1.3;
}
.sf-sheet-benefits {
  padding: 4mm 0;
}
.sf-sheet-h2 {
  color: var(--ink);
  font-family: var(--disp);
  font-size: 15pt;
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.15;
  text-transform: none;
  margin: 0;
}
.sf-sheet-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5mm;
  margin-top: 2.5mm;
}
.sf-sheet-card .sf-ico {
  color: #167d65;
  margin-bottom: 2mm;
}
.sf-sheet-card h3 {
  color: var(--ink);
  font-size: 10pt;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 0 1.8mm;
  text-transform: none;
}
.sf-sheet-card p,
.sf-sheet-step p {
  font-size: 8pt;
  line-height: 1.4;
  color: #4e5d6e;
  margin: 0;
}
.sf-sheet-band {
  background: #f3f6f8;
  padding: 4mm 5mm 3mm;
  border: 1px solid #e3e9ee;
  border-radius: 3mm;
}
.sf-sheet-sectionhead {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 3mm;
}
.sf-sheet-sectionhead .sf-sheet-h2 {
  font-size: 13pt;
}
.sf-sheet-sectionhead > span {
  color: #5a6e80;
  font-size: 7pt;
  white-space: nowrap;
}
.sf-sheet-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5mm;
  margin-top: 3mm;
}
.sf-sheet-stephead {
  display: flex;
  align-items: flex-start;
  gap: 2mm;
  margin-bottom: 2mm;
}
.sf-sheet-stepnum {
  color: #19765f;
  font-size: 16pt;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.05em;
}
.sf-sheet-stephead h3 {
  color: var(--ink);
  font-size: 8.5pt;
  font-weight: 700;
  line-height: 1.25;
  text-transform: none;
  margin: 0;
}
.sf-sheet-howfoot {
  border-top: 1px solid #dce4eb;
  padding-top: 2.5mm;
  margin-top: 3mm;
  text-align: center;
  color: #304e66;
  font-size: 8pt;
  font-weight: 600;
  line-height: 1.3;
}
.sf-sheet-close {
  display: grid;
  grid-template-columns: 1.08fr 1fr;
  gap: 5mm;
  align-items: center;
  margin-top: 4mm;
  padding: 4mm 5mm;
  background: #102b49;
  color: #fff;
  border-radius: 3mm;
}
.sf-sheet-close .sf-sheet-eyebrow {
  color: #a9d9f8;
  font-size: 6.5pt;
  margin-bottom: 2mm;
}
.sf-sheet-closeh {
  color: #fff;
  font-size: 17pt;
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.12;
  text-transform: none;
  margin: 0;
}
.sf-sheet-closesub {
  color: #d2dfeb;
  font-size: 8pt;
  line-height: 1.4;
  margin-top: 2mm;
}
.sf-sheet-apply {
  display: inline-flex;
  align-items: center;
  gap: 3mm;
  background: #73e2bc;
  color: #102b49;
  border-radius: 1.5mm;
  padding: 2mm 3mm;
  margin-top: 2mm;
  font-size: 8pt;
  font-weight: 700;
  line-height: 1.2;
}
.sf-sheet-apply .sf-ico {
  width: 4mm;
  height: 4mm;
}
.sf-sheet-repblock {
  border-left: 1px solid #416079;
  padding-left: 5mm;
  min-width: 0;
  overflow-wrap: anywhere;
}
.sf-sheet-contactlabel {
  display: block;
  color: #a9d9f8;
  font-size: 7pt;
  line-height: 1.3;
}
.sf-sheet-repname {
  display: block;
  color: #fff;
  font-size: 12pt;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 1.5mm;
}
.sf-sheet-repphone {
  display: block;
  color: #73e2bc;
  font-size: 16pt;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.3;
  margin-top: 1.5mm;
}
.sf-sheet-repline,
.sf-sheet-applyurl {
  display: block;
  color: #e2ebf5;
  font-size: 8pt;
  line-height: 1.4;
  margin-top: 1.2mm;
}
.sf-sheet-applyurl {
  color: #fff;
  font-weight: 600;
  margin-top: 3mm;
}
.sf-sheet-footline {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 3mm;
  margin-top: 3mm;
  font-size: 6.5pt;
  line-height: 1.4;
  color: #526375;
}
.sf-sheet-footline b {
  color: #167d65;
  font-weight: 700;
}
.sf-sheet-legal {
  font-size: 6.5pt;
  line-height: 1.4;
  color: #657384;
  margin: 1.5mm 0 0;
}
@media print {
  /* The app shell paints a page background; a printer would render it as a grey
     wash across every margin. */
  .sf-root {
    background: #fff;
  }
  .sf-screen {
    display: none !important;
  }
  .sf-sheet {
    display: block !important;
  }
  /* Both selectors: the on-screen preview sets these at a higher specificity, and
     printing from the preview must not carry its paper padding into the page box. */
  .sf-sheet-inner,
  .sf-sheet-onscreen .sf-sheet-inner {
    width: 186mm;
    max-width: 100%;
    min-height: 0;
    margin: 0 auto;
    box-shadow: none;
    padding: 0;
  }
  .sf-sheet-onscreen {
    background: #fff;
    padding: 0;
    min-height: 0;
    overflow: visible;
  }
  .sf-sheet-inner a::after {
    content: none !important;
  }
  .sf-sheet-head,
  .sf-sheet-hero,
  .sf-sheet-proof,
  .sf-sheet-benefits,
  .sf-sheet-band,
  .sf-sheet-close {
    break-inside: avoid;
  }
  /* Colour blocks are the design here, not decoration; Chrome and Safari drop them
     otherwise and the sheet prints as grey text on white. */
  .sf-sheet,
  .sf-sheet * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>

<!-- Unscoped on purpose: everything below reaches outside this component — App.vue's
     shell, the third-party widgets bolted onto <body>, and Bootstrap's own print block.
     All of it is gated on the body class this page adds and removes, so the rest of the
     site keeps printing exactly as it did. -->
<style>
body.sf-standalone #app > footer {
  display: none !important;
}

@media print {
  /* App.vue's preloader is a fixed 100vw × 100vh white panel at z-index 1031, faded out
     by jQuery only once window.load fires. Print before that lands and the whole sheet
     is rendered under a white veil — the PDF carries every glyph and none of them are
     readable. Nothing on a handout wants it, or the chat bubble, so neither prints. */
  body.sf-standalone .loader-wrapper,
  body.sf-standalone #chat-widget-container {
    display: none !important;
  }
  /* Bootstrap's print block forces `body{min-width:992px!important}`. The sheet is laid
     out in millimetres against the real page, so a 992px layout box leaves Chrome to
     shrink the page to fit — it printed at 73%, which is 9pt body copy landing as 6.6pt
     and the 7pt legal line as 5.1pt. */
  body.sf-standalone,
  body.sf-standalone .container {
    min-width: 0 !important;
  }
}

/* Kept below the scoped block, and so below Bootstrap's own print block, which asks for
   `size: a3`: between two @page rules it is source order that decides. */
@page {
  size: auto;
  margin: 12mm;
}
</style>



// WEBPACK FOOTER //
// src/pages/for_shops.vue